import { ComponentType } from 'react';

import { StyledSecondaryButton } from 'modules/messagingInsights/components/FilterList/Styled';
import { ESFieldTypeMap } from 'modules/workflows/types/fields';
import { Spinner } from 'ui/spinner/Spinner';
import { Add16 } from 'ui/icons/action/Add16';
import { EVENT_TYPES } from 'utils/dataTypes';

import {
  getComparators,
  getQueryStub,
} from 'modules/workflowNodeEditors/components/startNode/utils';
import { SearchQueriesValidationErrors } from '../RuleBuilder/types';
import { FilterInput } from './FilterInput/FilterInput';
import { AddButtonPrefix, StyledNest } from './FilterInputsList.styled';
import { NestComponentProps, PropertyMapConfig, Query } from './FilterInputsList.types';
import {
  configurePropertyMap,
  matchCustomEvents,
  removeIdsFromMap,
} from './FilterInputsList.utils';
import { useProperties } from './useProperties';

// set.has() lookups are more performant than array.includes()
export const CUSTOM_EVENT_SYSTEM_FIELDS = new Set(['eventUpdatedAt', 'createdAt', 'eventName']);

const DEFAULT_DISPLAY_TEXT = {
  addText: 'Add rule',
};

type FilterInputsListProps = {
  combinatorText?: string;
  dataType: string;
  initialQueries?: Query[];
  queries: Partial<Query>[];
  /** used to determine the correct field input, typeahead vs dropdown */
  trigger: string;
  onSearchQueriesUpdate: (queries: Partial<Query>[]) => void;
  canAddFilter?: boolean;
  canDelete?: boolean;
  NestComponent?:
    | ComponentType<React.PropsWithChildren<unknown>>
    | ComponentType<React.PropsWithChildren<NestComponentProps>>
    | null;
  DeleteFilterComponent?: ComponentType<React.PropsWithChildren<unknown>>;
  queriesErrors?: SearchQueriesValidationErrors;
  /** used to determine the the dropdown options to show when dataType is custom event */
  propertyMapObjectFilterString?: string;
  propertyMapDisallowedIds?: string[];
  isDisabled?: boolean;
  /** custom text to use for "add rule" button */
  displayText?: Record<string, string>;
  /** to be used on dark backgrounds, filters background is white on hover */
  isDarkTheme?: boolean;
  /** to be used for Property Changes rules to restyle the Filter Row */
  isPropertyChanges?: boolean;
  /** custom config for generating filter dropdown fields (combination of propertyMapObjectFilterString and propertyMapDisallowedIds ) */
  propertyMapConfig?: PropertyMapConfig;
};

export const FilterInputsList = ({
  combinatorText,
  dataType,
  initialQueries = [] as Query[],
  queries = [] as Partial<Query>[],
  trigger,
  onSearchQueriesUpdate,
  canAddFilter = true,
  canDelete = true,
  NestComponent = StyledNest,
  DeleteFilterComponent,
  queriesErrors = {},
  propertyMapObjectFilterString = '',
  propertyMapDisallowedIds = [],
  isDisabled = false,
  displayText = DEFAULT_DISPLAY_TEXT,
  isDarkTheme = false,
  isPropertyChanges = false,
  propertyMapConfig,
}: FilterInputsListProps) => {
  // TODO: move to custom event component wrapper WF-5455
  const filterPropertyMap = (map: ESFieldTypeMap) => {
    // two operations here: one to remove any that are disallowed based on propertyMapDisallowedIds,
    // and another to filter out any that don't match filterString
    const hasDisallowedList = propertyMapDisallowedIds.length > 0;

    const mapWithoutDisallowed = hasDisallowedList
      ? removeIdsFromMap(map, propertyMapDisallowedIds)
      : map;
    if (!propertyMapObjectFilterString) return mapWithoutDisallowed;
    return matchCustomEvents(mapWithoutDisallowed, propertyMapObjectFilterString);
  };
  const { isShown, propertyMap, propertyOptions, handleQueryUpdate } = useProperties({
    dataType,
    propertyMapDisallowedIds,
    onSearchQueriesUpdate,
    queries,
    initialQueries,
    filterPropertyMap: dataType === EVENT_TYPES.CUSTOM_EVENT ? filterPropertyMap : undefined, // to be refactored with custom event component wrapper WF-5455
    ...(propertyMapConfig && {
      propertyMapConfig,
      filterPropertyMap: configurePropertyMap,
    }),
  });
  return !isShown || !propertyMap || !propertyOptions ? (
    <Spinner />
  ) : (
    <div>
      {queries.map((query, idx) => {
        return (
          <div key={`${query.field}-${idx}`}>
            <FilterInput
              comparators={getComparators(propertyMap[query.field as string])}
              onChange={handleQueryUpdate}
              id={`${query.field}-${idx}`}
              idx={idx}
              onRemove={() =>
                onSearchQueriesUpdate([...queries.slice(0, idx), ...queries.slice(idx + 1)])
              }
              properties={propertyOptions}
              query={query}
              errors={queriesErrors?.[idx] || {}}
              hasError={false} // deprecated, kept in place to not break anything
              trigger={trigger}
              canDelete={canDelete}
              DeleteFilterComponent={DeleteFilterComponent}
              NestComponent={NestComponent}
              isDisabled={isDisabled}
              isDarkTheme={isDarkTheme}
              isPropertyChanges={isPropertyChanges}
            />
          </div>
        );
      })}
      {canAddFilter && (
        <>
          {/* only show combinator text if there is at least one query added */}
          {queries?.length > 0 && <AddButtonPrefix>{combinatorText}</AddButtonPrefix>}

          <StyledSecondaryButton
            isDisabled={isDisabled}
            data-test="filter-list-add-filter"
            onClick={() => onSearchQueriesUpdate([...queries, getQueryStub(dataType)])}
          >
            <Add16 />
            {displayText.addText}
          </StyledSecondaryButton>
        </>
      )}
    </div>
  );
};
